/* eslint-disable no-param-reassign */
import { v4 as uuidv4 } from 'uuid';
import { PAGE_TYPE } from '@/app/common/pageType.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { cancelPendingCSWRefreshes } from '@/app/common/cswRefresh.ts';
import { createClickId, isEmptyObject } from '@/app/components/search/Utilities.ts';
import type React from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { SearchParams } from '@/server/searchParams.ts';
import { getSearchParamsFromQueryString, removeParameters, updateUrl } from '@/app/common/urlParametersUtils.ts';
import { type PageWrapperData } from '@/app/common/createPageWrapperData.ts';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import {
  createSearchAdditionalParameterPayload,
  shouldTrackAdditionalParams,
} from '@/app/common/starTrack/events/searchAdditionalParameter.ts';
import { trackingSearchRequested } from '@/app/common/tracking/trackingSearchRequested.ts';
import { Context, Platform } from '@/app/common/starTrack/events/utils/starTrack.types.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { type BrandedProps, createHotelDetailData } from '@/app/hotel-detail/createHotelDetailData.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { SearchPageData } from '@/app/search/createSearchPageData.ts';
import { createQueryString } from '@/utils/queryUtils';
import { createPlatformSearchId } from '@/utils/searchIdUtils';
import { mergeSearchParams } from '@/app/common/mergeSearchParams.ts';
import { getDateWithoutTimeFromYYYYMMDD } from '@/app/components/search/utils/dates/getDateWithoutTime.ts';
import getCSWParticipants from '@/app/common/CSW/services/adapters/getCSWParticipants.ts';
import type { SearchInfoData, SearchInfoDetailData } from '@/app/search/types.ts';
import type { StarTrack } from '@/app/common/starTrack/startrack.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { scrollToTop } from '@/app/common/utils/scroll.ts';
import { createFreshDeeplink } from '@/app/components/search/utils/buildDealCards/createHotelDetailDeeplink.ts';
import { fetchPageWrapperData } from '@/app/common/refreshPageWrapperData.ts';
import type { SubSourceType } from './starTrack/events/searchRequested';
import { BASE_PATH } from '../components/search/utils/path';
import { addBrowserHistory } from './browserHistory';
import type { BackToResultsOptions, HandleHotelChosenOptions } from './commonClientProvider';
import type { CreatePageWrapperClientSearch, HandleHotelChosenData } from './PageWrapperClientTypes';
import type { PeopleData } from '../components/search/utils';
import type { DefaultValuesCSW, SupportedTabId } from './CSW/services/types';
import getCSWTabId from './CSW/services/adapters/getCSWTabId';

const goToDetailPage = (deepLink?: string) => {
  if (!deepLink) return;
  try {
    if (canUseDOM) window.open(`${deepLink}`, '_blank');
  } catch (e) {
    console.log(e);
  }
};

const removeDialogScrollBlock = () => {
  // some navigation steps may lead to a scroll lock because in the current step the modal is opened and not closed to go to the next step.
  // in this case the scroll lock is removed
  document.body.style.position = 'initial';
  document.body.style.overflow = 'auto';
};

type HotelChosenOptions = {
  shouldAddBrowserHistory: boolean;
  clickId?: string;
  refreshSearch?: boolean;
  subSourceType?: SubSourceType;
  koProviderId?: string;
  scrollToTop?: boolean;
  dealCard?: DealCard;
};

type CreatePageWrapperClientDetailsProps = {
  commonData: CommonData;
  localization: Localization;
  pageWrapperData: PageWrapperData;
  searchPageData: SearchPageData;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  pageType: PAGE_TYPE;
  hotelDetailData: BrandedProps;
  hotelId: string;
  hotelIdRef: RefObject<string>;
  transportId?: string;
  accommodationOnly: boolean;
  mealPlanIdRef: RefObject<string | undefined>;
  mealPlanCodeRef: RefObject<string | undefined>;
  brandedFareIdRef: RefObject<string | undefined>;
  scrollPosSearch: RefObject<number>;
  pageWrapperClientSearch: ReturnType<CreatePageWrapperClientSearch>;
  starTrack: StarTrack;
  setHotelDetailData: React.Dispatch<React.SetStateAction<BrandedProps>>;
  setHotelId: React.Dispatch<React.SetStateAction<string>>;
  setPageType: React.Dispatch<React.SetStateAction<PAGE_TYPE>>;
  setPageWrapperData: Dispatch<SetStateAction<PageWrapperData>>;
  setDefaultValuesCSW: Dispatch<SetStateAction<DefaultValuesCSW>>;
};
export const createPageWrapperClientDetails = ({
  commonData,
  localization,
  pageWrapperData,
  searchPageData,
  searchInfoData,
  searchInfoDetailData,
  pageType,
  hotelDetailData,
  hotelId,
  hotelIdRef,
  transportId: transportIdProp,
  accommodationOnly,
  mealPlanIdRef,
  mealPlanCodeRef,
  brandedFareIdRef,
  scrollPosSearch,
  pageWrapperClientSearch,
  starTrack,
  setHotelDetailData,
  setHotelId,
  setPageType,
  setPageWrapperData,
  setDefaultValuesCSW,
}: CreatePageWrapperClientDetailsProps) => {
  const { userUid } = commonData;
  const { searchMode } = searchInfoData;

  const handleHotelChosenBaseOnNewTab = (hotelIdChosen: string, options: HotelChosenOptions) => {
    const { clickId, refreshSearch, subSourceType, koProviderId, dealCard: dealCardChosen } = options;
    const [opxLink, hotelDetailDeepLink] = createFreshDeeplink({
      hotelIdChosen,
      dealCardChosen,
      searchPageData,
      pageType,
      clickId,
      refreshSearch,
      subSourceType,
      koProviderId,
      commonData,
      preClick: false,
    });

    goToDetailPage(opxLink || hotelDetailDeepLink);
  };

  const handleHotelChosenBase = (hotelIdChosen: string, options: HotelChosenOptions) => {
    const { clickId, refreshSearch, subSourceType, koProviderId, dealCard: dealCardChosen } = options;
    const [opxLink, hotelDetailDeepLink] = createFreshDeeplink({
      hotelIdChosen,
      dealCardChosen,
      searchPageData,
      pageType,
      clickId,
      refreshSearch,
      subSourceType,
      koProviderId,
      commonData,
      preClick: false,
    });

    if (opxLink) {
      goToDetailPage(opxLink);
      return;
    }

    if (hotelDetailDeepLink && options.shouldAddBrowserHistory) {
      const urlNextPage = new URL(hotelDetailDeepLink, window.location.href);
      addBrowserHistory(urlNextPage);
    }

    if (
      isEmptyObject(hotelDetailData) ||
      hotelIdChosen !== hotelIdRef.current ||
      hotelDetailData.initialParams.hdpSearchId !== pageWrapperData.searchInfoData.searchId
    ) {
      const hotelDetailDataResponse = createHotelDetailData({
        hotelId: hotelIdChosen,
        transportId: transportIdProp,
        pageWrapperData,
        searchParams: getSearchParams(),
      });
      setHotelDetailData(hotelDetailDataResponse);
    }

    setHotelId(hotelIdChosen);

    setPageType(PAGE_TYPE.HOTEL_DETAIL);
    if (options.scrollToTop) {
      scrollToTop();
    }
  };

  // MPH-646: in Evo the flightClickId identifies one flight-viewing session — every event fired
  // while a flight selection is shown (CHOOSE_FLIGHT_SHOWN, BRANDED_FARES_*, the book-click funnel
  // events and CLICK2CHECKOUT) joins on it. A new session starts on every entry to the flight page
  // (rotated here, in the navigation handlers) and on every applied flight change
  // (useChangeTransportHandler); direct SSR landings are seeded by createHotelDetailData.
  const rotateEvoFlightClickId = () => {
    if (localization.configValuesApp?.evolutionEnabled && !accommodationOnly) {
      setHotelDetailData((prev) => ({
        ...prev,
        initialParams: { ...prev.initialParams, flightClickId: uuidv4().slice(0, 32) },
      }));
    }
  };

  const handleRoomChosenBase = ({
    roomOfferId,
    mealPlanId,
    mealPlanCode,
    shouldAddBrowserHistory,
  }: {
    roomOfferId: string;
    mealPlanId: string;
    mealPlanCode: string;
    shouldAddBrowserHistory: boolean;
    isBreadcrumb?: boolean;
  }) => {
    if (shouldAddBrowserHistory) {
      const currentParams = getSearchParamsFromQueryString(window.location.search);
      const searchParamsNextPage = {
        ...removeParameters(currentParams, [URLParameters.MODAL, URLParameters.BRANDED_FARE_ID]),
        [URLParameters.ROOM_ID]: roomOfferId,
        [URLParameters.MEALPLAN_ID]: mealPlanId,
        [URLParameters.MEALPLAN_CODE]: mealPlanCode,
        pageType: accommodationOnly ? PAGE_TYPE.REVIEW : PAGE_TYPE.TRANSPORT_DETAIL,
      } as SearchParams;
      const urlNextPage = new URL(
        `${BASE_PATH}/${hotelId}?${createQueryString(searchParamsNextPage)}`,
        window.location.href
      );
      addBrowserHistory(urlNextPage);
      mealPlanIdRef.current = mealPlanId;
      mealPlanCodeRef.current = mealPlanCode;
    }

    rotateEvoFlightClickId();
    setPageType(accommodationOnly ? PAGE_TYPE.REVIEW : PAGE_TYPE.TRANSPORT_DETAIL);
  };

  const handleTransportChosenBase = ({
    transportIdChosen,
    shouldAddBrowserHistory,
    brandedFareId,
  }: {
    transportIdChosen: string;
    brandedFareId?: string;
    shouldAddBrowserHistory: boolean;
  }) => {
    if (shouldAddBrowserHistory) {
      const currentParams = getSearchParamsFromQueryString(window.location.search);
      const searchParamsNextPage = {
        ...removeParameters(currentParams, [URLParameters.MODAL]),
        [URLParameters.TRANSPORT_ID]: transportIdChosen,
        ...(brandedFareId ? { [URLParameters.BRANDED_FARE_ID]: brandedFareId } : undefined),
        pageType: PAGE_TYPE.REVIEW,
      } as SearchParams;
      const urlNextPage = new URL(
        `${BASE_PATH}/${hotelId}?${createQueryString(searchParamsNextPage)}`,
        window.location.href
      );
      addBrowserHistory(urlNextPage);
      brandedFareIdRef.current = brandedFareId;
    }

    setPageType(PAGE_TYPE.REVIEW);
    setHotelDetailData((prev) => ({ ...prev, initialParams: { ...prev.initialParams, actionIdSeed: uuidv4() } }));
  };

  const handleTransportChosen = (transportId: string, brandedFareId?: string) => {
    removeDialogScrollBlock();
    handleTransportChosenBase({ transportIdChosen: transportId, brandedFareId, shouldAddBrowserHistory: true });
  };

  const handleFlightClicked = (flightClickId: string, elegibleBrandedFares: boolean) => {
    setHotelDetailData((prev) => ({
      ...prev,
      initialParams: { ...prev.initialParams, flightClickId, elegibleBrandedFares },
    }));
  };

  const handleHotelChosen = (hotelIdChosen: string, options?: HandleHotelChosenOptions): HandleHotelChosenData => {
    const shouldGenerateClickId = options?.generateClickId ?? true;
    if (pageType === PAGE_TYPE.SEARCH || pageType === PAGE_TYPE.SEARCH_DESTINATIONS) {
      scrollPosSearch.current = window.scrollY;
    }

    const deepLink = options?.dealCard?.deepLink;
    const deepLinkParams = getSearchParamsFromQueryString(deepLink);
    const searchParams = getSearchParams();
    const clickId = deepLinkParams[URLParameters.CLICK_ID]
      ? deepLinkParams[URLParameters.CLICK_ID]
      : shouldGenerateClickId
        ? createClickId(searchParams[URLParameters.SEARCH_ID], userUid)
        : '';

    if (options?.openInNewTab) {
      handleHotelChosenBaseOnNewTab(hotelIdChosen, {
        shouldAddBrowserHistory: true,
        clickId,
        dealCard: options?.dealCard,
        refreshSearch: options?.refreshSearch,
        subSourceType: options?.subSourceType,
        scrollToTop: options?.scrollToTop,
      });
    } else {
      handleHotelChosenBase(hotelIdChosen, {
        shouldAddBrowserHistory: true,
        clickId,
        dealCard: options?.dealCard,
        refreshSearch: options?.refreshSearch,
        subSourceType: options?.subSourceType,
        scrollToTop: options?.scrollToTop,
      });
    }

    return { clickId };
  };

  const handleRoomChosen = (
    roomOfferId: string,
    mealPlanId: string,
    mealPlanCode: string,
    defaultTransportId: string
  ) => {
    removeDialogScrollBlock();
    setHotelDetailData((prev) => ({
      ...prev,
      initialParams: {
        ...prev.initialParams,
        defaultTransportId: prev.initialParams.defaultTransportId ?? defaultTransportId,
      },
    }));
    handleRoomChosenBase({ roomOfferId, mealPlanId, mealPlanCode, shouldAddBrowserHistory: true });
  };

  const handleChangeStay = async (shouldAddBrowserHistory?: boolean, options?: BackToResultsOptions) => {
    removeDialogScrollBlock();
    pageWrapperClientSearch.handleSearchChangedBase({
      shouldAddBrowserHistory: shouldAddBrowserHistory || true,
      highlightedHotel: (options?.highlightHotel ?? true) ? Number(hotelId) : undefined,
      refreshSearch: true,
    });
  };

  const handleChangeRoom = (koProviderId?: string) => {
    removeDialogScrollBlock();
    handleHotelChosenBase(hotelId, { shouldAddBrowserHistory: true, koProviderId });
  };

  const handleChangeTransport = () => {
    removeDialogScrollBlock();
    const currentParams = getSearchParamsFromQueryString(window.location.search);
    brandedFareIdRef.current = undefined;
    const searchParamsNextPage = {
      ...removeParameters(currentParams, [URLParameters.BRANDED_FARE_ID]),
      pageType: PAGE_TYPE.TRANSPORT_DETAIL,
    } as SearchParams;
    const urlNextPage = new URL(
      `${BASE_PATH}/${hotelId}?${createQueryString(searchParamsNextPage)}`,
      window.location.href
    );
    addBrowserHistory(urlNextPage);
    setPageType(PAGE_TYPE.TRANSPORT_DETAIL);
  };

  const handleBackToResults = (options?: BackToResultsOptions) => {
    pageWrapperClientSearch.handleSearchChangedBase({
      shouldAddBrowserHistory: true,
      newPageType: PAGE_TYPE.SEARCH,
      highlightedHotel: (options?.highlightHotel ?? true) ? Number(hotelId) : undefined,
      refreshSearch: true,
    });
  };

  const handleRefreshSearchFromHotelDetails = async (params: {
    subSourceType?: SubSourceType;
    syncCSWDates?: { dateFrom: string; dateTo: string };
    syncCSWTravellers?: PeopleData;
    extraQueryParamsToSet?: { [key: string]: string }[];
    extraQueryParamsToRemove?: string[];
  }): Promise<string> => {
    const newSearchId = createPlatformSearchId(userUid || '');

    // Build the URL diff once and feed it to both the payload merge and `updateUrl` so they
    // can't drift. We need the merged params now (before the URL write) because the API payload
    // and the tracking event below both depend on the post-update state.
    const queryParamsToSet = [
      { [URLParameters.SEARCH_ID]: newSearchId },
      ...(params.subSourceType ? [{ [URLParameters.BF_SUB_SOURCE]: params.subSourceType }] : []),
      ...(params.extraQueryParamsToSet || []),
    ];
    const queryParamsToRemove = params.extraQueryParamsToRemove;

    const searchParamsNextPage = mergeSearchParams(getSearchParams(), {
      queryParamsToSet,
      queryParamsToRemove,
    });

    const pageWrapperDataUpdated = await fetchPageWrapperData({
      searchParams: searchParamsNextPage,
      traceParent: searchInfoData.searchTraceParent,
      commonData,
    });

    if (pageWrapperDataUpdated) {
      setPageWrapperData(pageWrapperDataUpdated);
    } else {
      clientAppLog({
        level: 'WARN',
        codeSourceName: 'pageWrapperClient handleRefreshSearchFromHotelDetails',
        logMessage: `Failed to refresh PageWrapperData for searchId: ${newSearchId}`,
        url: window.location.href,
      });
    }

    // Use the freshly-fetched pageWrapperData (correct AB test exclusions, searchInfoDetailData, etc.) for the
    // tracking call below rather than the values this closure was created with — those go stale as soon as this
    // handler fetches new data. Fall back to the stale values only if the fetch itself failed.
    const commonDataToUse = pageWrapperDataUpdated?.commonData || commonData;
    const localizationToUse = pageWrapperDataUpdated?.localization || localization;
    const searchInfoDataToUse = pageWrapperDataUpdated?.searchInfoData || searchInfoData;
    const searchInfoDetailDataToUse = pageWrapperDataUpdated?.searchInfoDetailData || searchInfoDetailData;

    updateUrl({ queryParamsToSet, queryParamsToRemove });

    if (shouldTrackAdditionalParams(searchParamsNextPage)) {
      starTrack.searchAdditionalParameter(
        createSearchAdditionalParameterPayload({ searchId: newSearchId, searchParams: searchParamsNextPage })
      );
    }
    trackingSearchRequested({
      starTrack,
      searchId: newSearchId,
      searchParams: searchParamsNextPage,
      searchInfoData: searchInfoDataToUse,
      searchInfoDetailData: searchInfoDetailDataToUse,
      commonData: commonDataToUse,
      localization: localizationToUse,
      context: Context.hotelDetail,
      internalHotelId: hotelId,
      platform: Platform.tsx,
      logText: '333 handleRefreshSearchFromHotelDetails',
      apexPerimeterFlag: localizationToUse.configValuesApp.apexEnabledForResults,
    });
    setHotelDetailData((prev) => ({
      ...prev,
      initialParams: { ...prev.initialParams, hdpSearchId: newSearchId, productDetailKey: uuidv4() },
    }));

    if (params.syncCSWDates || params.syncCSWTravellers) {
      const currentTabId: SupportedTabId = getCSWTabId(searchMode);

      // The dates and travellers chosen here win over a widget refresh still running.
      cancelPendingCSWRefreshes();
      setDefaultValuesCSW((prevDefaultValues) => ({
        ...prevDefaultValues,
        [currentTabId]: {
          ...prevDefaultValues[currentTabId as keyof DefaultValuesCSW],
          ...(params.syncCSWDates
            ? {
                isPartialStay: false,
                dates: {
                  ...prevDefaultValues[currentTabId as keyof DefaultValuesCSW].dates,
                  mode: 'calendar',
                  calendar: {
                    startDate: getDateWithoutTimeFromYYYYMMDD(params.syncCSWDates.dateFrom),
                    endDate: getDateWithoutTimeFromYYYYMMDD(params.syncCSWDates.dateTo),
                  },
                },
              }
            : undefined),
          ...(params.syncCSWTravellers ? { participants: getCSWParticipants(params.syncCSWTravellers) } : undefined),
        },
      }));
    }

    return newSearchId;
  };

  const handleEvoGoToCheckout = ({
    roomOfferId,
    mealPlanId,
    mealPlanCode,
    transportId,
    brandedFareId,
  }: {
    roomOfferId: string;
    mealPlanId: string;
    mealPlanCode: string;
    transportId?: string;
    brandedFareId?: string;
  }) => {
    const currentParams = getSearchParamsFromQueryString(window.location.search);
    const searchParamsNextPage = {
      ...removeParameters(currentParams, [
        URLParameters.MODAL,
        URLParameters.TRANSPORT_ID,
        URLParameters.BRANDED_FARE_ID,
      ]),
      [URLParameters.ROOM_ID]: roomOfferId,
      [URLParameters.MEALPLAN_ID]: mealPlanId,
      [URLParameters.MEALPLAN_CODE]: mealPlanCode,
      ...(transportId ? { [URLParameters.TRANSPORT_ID]: transportId } : undefined),
      ...(brandedFareId ? { [URLParameters.BRANDED_FARE_ID]: brandedFareId } : undefined),
      pageType: PAGE_TYPE.REVIEW,
    } as SearchParams;
    const urlNextPage = new URL(
      `${BASE_PATH}/${hotelId}?${createQueryString(searchParamsNextPage)}`,
      window.location.href
    );
    window.history.pushState({}, '', urlNextPage);
    // Keep the refs in step with the URL this navigation writes, the way handleRoomChosenBase does on the
    // legacy path. They feed RoomPanelModal's preselect, so without this a Hotel Only user who books from
    // the rooms step and comes back in-app would reopen the room modal on the page-load default (MPH-935).
    mealPlanIdRef.current = mealPlanId;
    mealPlanCodeRef.current = mealPlanCode;
    setPageType(PAGE_TYPE.REVIEW);
    setHotelDetailData((prev) => ({ ...prev, initialParams: { ...prev.initialParams, actionIdSeed: uuidv4() } }));
  };

  const handleEvoGoToChangeTransport = ({
    roomOfferId,
    mealPlanId,
    mealPlanCode,
  }: {
    roomOfferId: string;
    mealPlanId: string;
    mealPlanCode: string;
  }) => {
    const currentParams = getSearchParamsFromQueryString(window.location.search);
    const searchParamsNextPage = {
      ...removeParameters(currentParams, [
        URLParameters.MODAL,
        URLParameters.TRANSPORT_ID,
        URLParameters.BRANDED_FARE_ID,
      ]),
      [URLParameters.ROOM_ID]: roomOfferId,
      [URLParameters.MEALPLAN_ID]: mealPlanId,
      [URLParameters.MEALPLAN_CODE]: mealPlanCode,
      pageType: PAGE_TYPE.TRANSPORT_DETAIL,
    } as SearchParams;
    const urlNextPage = new URL(
      `${BASE_PATH}/${hotelId}?${createQueryString(searchParamsNextPage)}`,
      window.location.href
    );
    window.history.pushState({}, '', urlNextPage);
    rotateEvoFlightClickId();
    setPageType(PAGE_TYPE.TRANSPORT_DETAIL);
  };

  // MPH-646: baseline for FLIGHT_CLICKED's wasDefaultFlight — the flight the Evo flight page first
  // showed this detail visit (useTrackEvoChooseTransportShown). Captured at page-show rather than at
  // the rooms→flights transition so reloads/deep links onto the flight page get a baseline too.
  // Keep-first: post-change re-shows and re-entries must not overwrite the original baseline.
  const handleEvoDefaultTransportShown = (transportId: string) => {
    setHotelDetailData((prev) => ({
      ...prev,
      initialParams: {
        ...prev.initialParams,
        defaultTransportId: prev.initialParams.defaultTransportId || transportId,
      },
    }));
  };

  const handleEvoGoToHotelDetails = (mealPlan?: { mealPlanId: string; mealPlanCode: string }) => {
    const currentParams = getSearchParamsFromQueryString(window.location.search);
    // Back to the rooms step resets the cancellation choice (the in-memory selection is reset by the
    // caller, useEvoModifyHotel): the caller passes the reset meal plan so a reload restores the same
    // board class with the default cancellation option; without one the meal plan params are dropped
    // and the room's overall default surfaces. The room itself stays selected (ROOM_ID is kept).
    const searchParamsNextPage = {
      ...removeParameters(currentParams, [URLParameters.MEALPLAN_ID, URLParameters.MEALPLAN_CODE]),
      ...(mealPlan
        ? {
            [URLParameters.MEALPLAN_ID]: mealPlan.mealPlanId,
            [URLParameters.MEALPLAN_CODE]: mealPlan.mealPlanCode,
          }
        : undefined),
      pageType: PAGE_TYPE.HOTEL_DETAIL,
    } as SearchParams;
    const urlNextPage = new URL(
      `${BASE_PATH}/${hotelId}?${createQueryString(searchParamsNextPage)}`,
      window.location.href
    );
    window.history.pushState({}, '', urlNextPage);
    setPageType(PAGE_TYPE.HOTEL_DETAIL);
  };

  return {
    handleHotelChosenBase,
    handleRoomChosenBase,
    handleTransportChosenBase,
    handleBackToResults,
    handleChangeTransport,
    handleChangeRoom,
    handleChangeStay,
    handleTransportChosen,
    handleFlightClicked,
    handleHotelChosen,
    handleRoomChosen,
    handleRefreshSearchFromHotelDetails,
    handleEvoGoToCheckout,
    handleEvoGoToChangeTransport,
    handleEvoGoToHotelDetails,
    handleEvoDefaultTransportShown,
  };
};
